import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const minimumCalculatorDate = "2019-07-01";
const maximumCalculatorDate = "2027-06-30";

const legacySalarySlabs = [
  { max: 600000, base: 0, rate: 0, over: 0, label: "Up to Rs. 600,000" },
  { max: 1200000, base: 0, rate: 0.05, over: 600000, label: "Rs. 600,001 to Rs. 1,200,000" },
  { max: 1800000, base: 30000, rate: 0.1, over: 1200000, label: "Rs. 1,200,001 to Rs. 1,800,000" },
  { max: 2500000, base: 90000, rate: 0.15, over: 1800000, label: "Rs. 1,800,001 to Rs. 2,500,000" },
  { max: 3500000, base: 195000, rate: 0.175, over: 2500000, label: "Rs. 2,500,001 to Rs. 3,500,000" },
  { max: 5000000, base: 370000, rate: 0.2, over: 3500000, label: "Rs. 3,500,001 to Rs. 5,000,000" },
  { max: 8000000, base: 670000, rate: 0.225, over: 5000000, label: "Rs. 5,000,001 to Rs. 8,000,000" },
  { max: 12000000, base: 1345000, rate: 0.25, over: 8000000, label: "Rs. 8,000,001 to Rs. 12,000,000" },
  { max: 30000000, base: 2345000, rate: 0.275, over: 12000000, label: "Rs. 12,000,001 to Rs. 30,000,000" },
  { max: 50000000, base: 7295000, rate: 0.3, over: 30000000, label: "Rs. 30,000,001 to Rs. 50,000,000" },
  { max: 75000000, base: 13295000, rate: 0.325, over: 50000000, label: "Rs. 50,000,001 to Rs. 75,000,000" },
  { max: Infinity, base: 21420000, rate: 0.35, over: 75000000, label: "Above Rs. 75,000,000" },
];

const salaryTaxYears = [
  {
    id: "fy-2026-27",
    label: "FY 2026-27 (Tax Year 2027)",
    period: "Jul 2026 to Jun 2027",
    startDate: "2026-07-01",
    endDate: "2027-06-30",
    note: "Salaried surcharge withdrawn",
    slabs: [
      { max: 600000, base: 0, rate: 0, over: 0, label: "Up to Rs. 600,000" },
      { max: 1200000, base: 0, rate: 0.01, over: 600000, label: "Rs. 600,001 to Rs. 1,200,000" },
      { max: 2200000, base: 6000, rate: 0.11, over: 1200000, label: "Rs. 1,200,001 to Rs. 2,200,000" },
      { max: 3200000, base: 116000, rate: 0.2, over: 2200000, label: "Rs. 2,200,001 to Rs. 3,200,000" },
      { max: 4100000, base: 316000, rate: 0.25, over: 3200000, label: "Rs. 3,200,001 to Rs. 4,100,000" },
      { max: 5600000, base: 541000, rate: 0.29, over: 4100000, label: "Rs. 4,100,001 to Rs. 5,600,000" },
      { max: 7000000, base: 976000, rate: 0.32, over: 5600000, label: "Rs. 5,600,001 to Rs. 7,000,000" },
      { max: Infinity, base: 1424000, rate: 0.35, over: 7000000, label: "Above Rs. 7,000,000" },
    ],
  },
  {
    id: "fy-2025-26",
    label: "FY 2025-26 (Tax Year 2026)",
    period: "Jul 2025 to Jun 2026",
    startDate: "2025-07-01",
    endDate: "2026-06-30",
    surchargeThreshold: 10000000,
    surchargeRate: 0.09,
    slabs: [
      { max: 600000, base: 0, rate: 0, over: 0, label: "Up to Rs. 600,000" },
      { max: 1200000, base: 0, rate: 0.01, over: 600000, label: "Rs. 600,001 to Rs. 1,200,000" },
      { max: 2200000, base: 6000, rate: 0.11, over: 1200000, label: "Rs. 1,200,001 to Rs. 2,200,000" },
      { max: 3200000, base: 116000, rate: 0.23, over: 2200000, label: "Rs. 2,200,001 to Rs. 3,200,000" },
      { max: 4100000, base: 346000, rate: 0.3, over: 3200000, label: "Rs. 3,200,001 to Rs. 4,100,000" },
      { max: Infinity, base: 616000, rate: 0.35, over: 4100000, label: "Above Rs. 4,100,000" },
    ],
  },
  {
    id: "fy-2024-25",
    label: "FY 2024-25 (Tax Year 2025)",
    period: "Jul 2024 to Jun 2025",
    startDate: "2024-07-01",
    endDate: "2025-06-30",
    slabs: [
      { max: 600000, base: 0, rate: 0, over: 0, label: "Up to Rs. 600,000" },
      { max: 1200000, base: 0, rate: 0.05, over: 600000, label: "Rs. 600,001 to Rs. 1,200,000" },
      { max: 2200000, base: 30000, rate: 0.15, over: 1200000, label: "Rs. 1,200,001 to Rs. 2,200,000" },
      { max: 3200000, base: 180000, rate: 0.25, over: 2200000, label: "Rs. 2,200,001 to Rs. 3,200,000" },
      { max: 4100000, base: 430000, rate: 0.3, over: 3200000, label: "Rs. 3,200,001 to Rs. 4,100,000" },
      { max: Infinity, base: 700000, rate: 0.35, over: 4100000, label: "Above Rs. 4,100,000" },
    ],
  },
  {
    id: "fy-2023-24",
    label: "FY 2023-24 (Tax Year 2024)",
    period: "Jul 2023 to Jun 2024",
    startDate: "2023-07-01",
    endDate: "2024-06-30",
    slabs: [
      { max: 600000, base: 0, rate: 0, over: 0, label: "Up to Rs. 600,000" },
      { max: 1200000, base: 0, rate: 0.025, over: 600000, label: "Rs. 600,001 to Rs. 1,200,000" },
      { max: 2400000, base: 15000, rate: 0.125, over: 1200000, label: "Rs. 1,200,001 to Rs. 2,400,000" },
      { max: 3600000, base: 165000, rate: 0.225, over: 2400000, label: "Rs. 2,400,001 to Rs. 3,600,000" },
      { max: 6000000, base: 435000, rate: 0.275, over: 3600000, label: "Rs. 3,600,001 to Rs. 6,000,000" },
      { max: Infinity, base: 1095000, rate: 0.35, over: 6000000, label: "Above Rs. 6,000,000" },
    ],
  },
  {
    id: "fy-2022-23",
    label: "FY 2022-23 (Tax Year 2023)",
    period: "Jul 2022 to Jun 2023",
    startDate: "2022-07-01",
    endDate: "2023-06-30",
    slabs: [
      { max: 600000, base: 0, rate: 0, over: 0, label: "Up to Rs. 600,000" },
      { max: 1200000, base: 0, rate: 0.025, over: 600000, label: "Rs. 600,001 to Rs. 1,200,000" },
      { max: 2400000, base: 15000, rate: 0.125, over: 1200000, label: "Rs. 1,200,001 to Rs. 2,400,000" },
      { max: 3600000, base: 165000, rate: 0.2, over: 2400000, label: "Rs. 2,400,001 to Rs. 3,600,000" },
      { max: 6000000, base: 405000, rate: 0.25, over: 3600000, label: "Rs. 3,600,001 to Rs. 6,000,000" },
      { max: 12000000, base: 1005000, rate: 0.325, over: 6000000, label: "Rs. 6,000,001 to Rs. 12,000,000" },
      { max: Infinity, base: 2955000, rate: 0.35, over: 12000000, label: "Above Rs. 12,000,000" },
    ],
  },
  {
    id: "fy-2021-22",
    label: "FY 2021-22 (Tax Year 2022)",
    period: "Jul 2021 to Jun 2022",
    startDate: "2021-07-01",
    endDate: "2022-06-30",
    slabs: legacySalarySlabs,
  },
  {
    id: "fy-2020-21",
    label: "FY 2020-21 (Tax Year 2021)",
    period: "Jul 2020 to Jun 2021",
    startDate: "2020-07-01",
    endDate: "2021-06-30",
    slabs: legacySalarySlabs,
  },
  {
    id: "fy-2019-20",
    label: "FY 2019-20 (Tax Year 2020)",
    period: "Jul 2019 to Jun 2020",
    startDate: "2019-07-01",
    endDate: "2020-06-30",
    slabs: legacySalarySlabs,
  },
];

const defaultTaxYearId = "fy-2026-27";
const taxYearsById = Object.fromEntries(salaryTaxYears.map((taxYear) => [taxYear.id, taxYear]));

function formatCurrency(value) {
  return new Intl.NumberFormat("en-PK", {
    maximumFractionDigits: 0,
    style: "currency",
    currency: "PKR",
  }).format(Math.max(0, Math.round(value)));
}

function formatRate(rate) {
  return `${(rate * 100).toFixed(rate * 100 === Math.round(rate * 100) ? 0 : 1)}%`;
}

function displayCurrency(value, hasResult) {
  return hasResult ? formatCurrency(value) : "-";
}

function parseDate(value) {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function toDateInputValue(date) {
  return date.toISOString().slice(0, 10);
}

function addDays(date, days) {
  return new Date(date.getTime() + days * MS_PER_DAY);
}

function daysInclusive(startDate, endDate) {
  return Math.round((endDate.getTime() - startDate.getTime()) / MS_PER_DAY) + 1;
}

function getMonthStart(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function getMonthEnd(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
}

function getFiscalYearId(date) {
  const fiscalStartYear = date.getUTCMonth() >= 6 ? date.getUTCFullYear() : date.getUTCFullYear() - 1;
  const fiscalEndYear = fiscalStartYear + 1;
  return `fy-${fiscalStartYear}-${String(fiscalEndYear).slice(2)}`;
}

function calculateAnnualSalaryTax(annualSalary, taxYear) {
  const safeAnnualSalary = Math.max(0, Number(annualSalary) || 0);
  const slab = taxYear.slabs.find((item) => safeAnnualSalary <= item.max) || taxYear.slabs[taxYear.slabs.length - 1];
  const slabTax = slab.base + Math.max(0, safeAnnualSalary - slab.over) * slab.rate;
  const surcharge =
    taxYear.surchargeThreshold && safeAnnualSalary > taxYear.surchargeThreshold
      ? slabTax * taxYear.surchargeRate
      : 0;
  const annualTax = slabTax + surcharge;

  return {
    annualSalary: safeAnnualSalary,
    annualTax,
    effectiveRate: safeAnnualSalary > 0 ? (annualTax / safeAnnualSalary) * 100 : 0,
    monthlySalary: safeAnnualSalary / 12,
    monthlyTax: annualTax / 12,
    netMonthly: safeAnnualSalary / 12 - annualTax / 12,
    slab,
    surcharge,
    taxYear,
  };
}

function calculateSalaryTax(monthlySalary, taxYear) {
  return calculateAnnualSalaryTax(Math.max(0, Number(monthlySalary) || 0) * 12, taxYear);
}

function calculateMultiYearTax(periods) {
  const errors = [];
  const incomeByYear = {};
  let totalDays = 0;

  periods.forEach((period, index) => {
    const startDate = parseDate(period.startDate);
    const endDate = parseDate(period.endDate);
    const monthlySalary = Math.max(0, Number(period.monthlySalary) || 0);

    if (!startDate || !endDate || monthlySalary <= 0) {
      errors.push(`Period ${index + 1}: enter start date, end date and monthly salary.`);
      return;
    }

    if (startDate > endDate) {
      errors.push(`Period ${index + 1}: end date must be after start date.`);
      return;
    }

    if (period.startDate < minimumCalculatorDate || period.endDate > maximumCalculatorDate) {
      errors.push(`Period ${index + 1}: dates must be between July 1, 2019 and June 30, 2027.`);
      return;
    }

    let cursor = startDate;
    while (cursor <= endDate) {
      const monthEnd = getMonthEnd(cursor);
      const segmentEnd = endDate < monthEnd ? endDate : monthEnd;
      const activeDays = daysInclusive(cursor, segmentEnd);
      const monthDays = daysInclusive(getMonthStart(cursor), monthEnd);
      const income = (monthlySalary * activeDays) / monthDays;
      const taxYearId = getFiscalYearId(cursor);

      incomeByYear[taxYearId] = (incomeByYear[taxYearId] || 0) + income;
      totalDays += activeDays;
      cursor = addDays(segmentEnd, 1);
    }
  });

  const yearlyBreakdown = Object.entries(incomeByYear)
    .map(([taxYearId, annualSalary]) => {
      const taxYear = taxYearsById[taxYearId];
      const result = taxYear ? calculateAnnualSalaryTax(annualSalary, taxYear) : null;

      return taxYear && result
        ? {
            taxYear,
            annualSalary: result.annualSalary,
            annualTax: result.annualTax,
            netSalary: result.annualSalary - result.annualTax,
            effectiveRate: result.effectiveRate,
            slab: result.slab,
            surcharge: result.surcharge,
          }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.taxYear.startDate.localeCompare(b.taxYear.startDate));

  const totalSalary = yearlyBreakdown.reduce((sum, item) => sum + item.annualSalary, 0);
  const totalTax = yearlyBreakdown.reduce((sum, item) => sum + item.annualTax, 0);

  return {
    errors,
    yearlyBreakdown,
    totalDays,
    totalSalary,
    totalTax,
    netSalary: totalSalary - totalTax,
    effectiveRate: totalSalary > 0 ? (totalTax / totalSalary) * 100 : 0,
  };
}

function createPeriod(index) {
  return {
    id: `period-${Date.now()}-${index}`,
    startDate: "2025-12-01",
    endDate: "2026-06-30",
    monthlySalary: "100000",
  };
}

export default function SalaryTaxCalculator() {
  const [mode, setMode] = useState("single");
  const [monthlySalary, setMonthlySalary] = useState("150000");
  const [calculatedSalary, setCalculatedSalary] = useState("150000");
  const [selectedTaxYearId, setSelectedTaxYearId] = useState(defaultTaxYearId);
  const [periods, setPeriods] = useState([createPeriod(1)]);
  const [submittedPeriods, setSubmittedPeriods] = useState(periods);
  const selectedTaxYear = taxYearsById[selectedTaxYearId] || taxYearsById[defaultTaxYearId];
  const result = useMemo(
    () => calculateSalaryTax(calculatedSalary, selectedTaxYear),
    [calculatedSalary, selectedTaxYear],
  );
  const multiYearResult = useMemo(() => calculateMultiYearTax(submittedPeriods), [submittedPeriods]);
  const hasResult = calculatedSalary !== "";
  const hasMultiYearResult = submittedPeriods.length > 0 && multiYearResult.errors.length === 0;

  function handleCalculate(event) {
    event.preventDefault();
    setCalculatedSalary(monthlySalary.trim());
  }

  function handleSalaryChange(event) {
    const nextSalary = event.target.value;
    setMonthlySalary(nextSalary);

    if (nextSalary === "") {
      setCalculatedSalary("");
    }
  }

  function handlePeriodChange(periodId, field, value) {
    setPeriods((currentPeriods) =>
      currentPeriods.map((period) => (period.id === periodId ? { ...period, [field]: value } : period)),
    );
  }

  function handleAddPeriod() {
    setPeriods((currentPeriods) => [...currentPeriods, createPeriod(currentPeriods.length + 1)]);
  }

  function handleRemovePeriod(periodId) {
    setPeriods((currentPeriods) => currentPeriods.filter((period) => period.id !== periodId));
  }

  function handleMultiYearCalculate(event) {
    event.preventDefault();
    setSubmittedPeriods(periods.map((period) => ({ ...period })));
  }

  return (
    <section id="salary-tax-calculator" className="section bg-slate-950 text-white">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.9fr_1.1fr] lg:items-start lg:px-8">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.12em] text-cyan-200">
            Salary Tax Calculator
          </p>
          <h2 className="mt-3 text-3xl font-semibold leading-tight sm:text-4xl">
            Estimate Pakistan salary tax from FY 2019-20 to FY 2026-27.
          </h2>
          <p className="mt-5 leading-8 text-slate-300">
            Select one fiscal year for a quick monthly estimate, or use multi-year mode to
            calculate total tax across custom salary periods and partial months.
          </p>
          <p className="mt-4 rounded-md border border-white/10 bg-white/10 p-4 text-sm leading-6 text-slate-300">
            This is a planning estimate only. Final tax can vary because of employer benefits,
            allowances, rebates, tax credits, arrears, bonuses or other income.
          </p>
        </div>

        <div className="rounded-md border border-white/15 bg-white p-5 text-slate-950 shadow-2xl sm:p-6">
          <div className="mb-6 grid grid-cols-2 rounded-md bg-slate-100 p-1 text-sm font-semibold">
            <button
              className={`rounded-md px-4 py-3 transition ${
                mode === "single" ? "bg-white text-cyan-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
              }`}
              type="button"
              onClick={() => setMode("single")}
            >
              Single Year
            </button>
            <button
              className={`rounded-md px-4 py-3 transition ${
                mode === "multi" ? "bg-white text-cyan-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
              }`}
              type="button"
              onClick={() => setMode("multi")}
            >
              Multi-Year
            </button>
          </div>

          {mode === "single" ? (
            <>
              <form className="grid gap-4" onSubmit={handleCalculate}>
                <div>
                  <label htmlFor="taxYear" className="block text-sm font-semibold text-slate-800">
                    Fiscal / tax year
                  </label>
                  <select
                    id="taxYear"
                    className="field mt-3"
                    value={selectedTaxYearId}
                    onChange={(event) => setSelectedTaxYearId(event.target.value)}
                  >
                    {salaryTaxYears.map((taxYear) => (
                      <option key={taxYear.id} value={taxYear.id}>
                        {taxYear.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="monthlySalary" className="block text-sm font-semibold text-slate-800">
                    Gross monthly salary
                  </label>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <div className="relative flex-1">
                      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-500">
                        Rs.
                      </span>
                      <input
                        id="monthlySalary"
                        className="field pl-12"
                        inputMode="numeric"
                        min="0"
                        type="number"
                        value={monthlySalary}
                        onChange={handleSalaryChange}
                        placeholder="150000"
                      />
                    </div>
                    <button className="btn-primary sm:min-w-36" type="submit">
                      Calculate
                    </button>
                  </div>
                </div>
              </form>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="calculator-metric">
                  <span>Monthly tax</span>
                  <strong>{displayCurrency(result.monthlyTax, hasResult)}</strong>
                </div>
                <div className="calculator-metric">
                  <span>Estimated take-home</span>
                  <strong>{displayCurrency(result.netMonthly, hasResult)}</strong>
                </div>
                <div className="calculator-metric">
                  <span>Annual tax</span>
                  <strong>{displayCurrency(result.annualTax, hasResult)}</strong>
                </div>
                <div className="calculator-metric">
                  <span>Effective rate</span>
                  <strong>{hasResult ? `${result.effectiveRate.toFixed(2)}%` : "-"}</strong>
                </div>
              </div>

              <div className="mt-5 rounded-md bg-slate-100 p-4">
                <div className="grid gap-3 text-sm sm:grid-cols-2">
                  <p>
                    <span className="block font-semibold text-slate-500">Annual salary</span>
                    <strong className="text-slate-950">{displayCurrency(result.annualSalary, hasResult)}</strong>
                  </p>
                  <p>
                    <span className="block font-semibold text-slate-500">Selected year</span>
                    <strong className="text-slate-950">{selectedTaxYear.label}</strong>
                  </p>
                  <p>
                    <span className="block font-semibold text-slate-500">Period</span>
                    <strong className="text-slate-950">{selectedTaxYear.period}</strong>
                  </p>
                  <p>
                    <span className="block font-semibold text-slate-500">Applicable slab</span>
                    <strong className="text-slate-950">
                      {hasResult ? result.slab.label : "Enter salary and press Calculate"}
                    </strong>
                  </p>
                </div>
                {hasResult && result.surcharge > 0 && (
                  <p className="mt-3 text-sm text-slate-600">
                    Includes estimated {formatRate(selectedTaxYear.surchargeRate)} surcharge on
                    calculated income tax because annual taxable income exceeds{" "}
                    {formatCurrency(selectedTaxYear.surchargeThreshold)}.
                  </p>
                )}
                {selectedTaxYear.note && (
                  <p className="mt-3 text-sm text-slate-600">{selectedTaxYear.note} for this selection.</p>
                )}
              </div>

              <SlabTable selectedTaxYear={selectedTaxYear} />
            </>
          ) : (
            <form onSubmit={handleMultiYearCalculate}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
                  <p className="font-semibold text-emerald-900">Partial Month Calculation</p>
                  <p className="mt-2 text-sm leading-6 text-emerald-800">
                    Salary is prorated for incomplete months based on actual calendar days.
                  </p>
                </div>
                <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
                  <p className="font-semibold text-blue-900">Available Fiscal Years</p>
                  <p className="mt-2 text-sm leading-6 text-blue-800">
                    Supports salary tax periods from July 1, 2019 to June 30, 2027.
                  </p>
                </div>
              </div>

              <div className="mt-5 grid gap-4">
                {periods.map((period, index) => (
                  <div key={period.id} className="rounded-md border border-slate-200 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <h3 className="text-lg font-semibold text-slate-950">Period {index + 1}</h3>
                      {periods.length > 1 && (
                        <button
                          className="text-sm font-semibold text-rose-700 hover:text-rose-900"
                          type="button"
                          onClick={() => handleRemovePeriod(period.id)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="mt-4 grid gap-4 lg:grid-cols-3">
                      <div>
                        <label className="block text-sm font-semibold text-slate-800" htmlFor={`${period.id}-start`}>
                          Start Date
                        </label>
                        <input
                          id={`${period.id}-start`}
                          className="field mt-2"
                          max={maximumCalculatorDate}
                          min={minimumCalculatorDate}
                          type="date"
                          value={period.startDate}
                          onChange={(event) => handlePeriodChange(period.id, "startDate", event.target.value)}
                        />
                        <p className="mt-2 text-xs text-slate-500">Between July 1, 2019 and June 30, 2027</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-800" htmlFor={`${period.id}-end`}>
                          End Date
                        </label>
                        <input
                          id={`${period.id}-end`}
                          className="field mt-2"
                          max={maximumCalculatorDate}
                          min={minimumCalculatorDate}
                          type="date"
                          value={period.endDate}
                          onChange={(event) => handlePeriodChange(period.id, "endDate", event.target.value)}
                        />
                        <p className="mt-2 text-xs text-slate-500">Between July 1, 2019 and June 30, 2027</p>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-800" htmlFor={`${period.id}-salary`}>
                          Monthly Salary (Rs.)
                        </label>
                        <input
                          id={`${period.id}-salary`}
                          className="field mt-2"
                          inputMode="numeric"
                          min="0"
                          type="number"
                          value={period.monthlySalary}
                          onChange={(event) => handlePeriodChange(period.id, "monthlySalary", event.target.value)}
                          placeholder="100000"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  className="inline-flex items-center justify-center rounded-md border border-dashed border-emerald-300 bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-800 transition hover:border-emerald-500 hover:bg-emerald-100"
                  type="button"
                  onClick={handleAddPeriod}
                >
                  Add Another Period
                </button>
                <button className="btn-primary sm:min-w-48" type="submit">
                  Calculate Total Tax
                </button>
              </div>

              {multiYearResult.errors.length > 0 && (
                <div className="mt-5 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-800">
                  {multiYearResult.errors.map((error) => (
                    <p key={error}>{error}</p>
                  ))}
                </div>
              )}

              {hasMultiYearResult && (
                <>
                  <div className="mt-6 grid gap-3 sm:grid-cols-2">
                    <div className="calculator-metric">
                      <span>Total tax</span>
                      <strong>{formatCurrency(multiYearResult.totalTax)}</strong>
                    </div>
                    <div className="calculator-metric">
                      <span>Total net salary</span>
                      <strong>{formatCurrency(multiYearResult.netSalary)}</strong>
                    </div>
                    <div className="calculator-metric">
                      <span>Total gross salary</span>
                      <strong>{formatCurrency(multiYearResult.totalSalary)}</strong>
                    </div>
                    <div className="calculator-metric">
                      <span>Effective rate</span>
                      <strong>{multiYearResult.effectiveRate.toFixed(2)}%</strong>
                    </div>
                  </div>

                  <div className="mt-5 overflow-x-auto rounded-md border border-slate-200">
                    <table className="w-full min-w-[680px] text-left text-sm">
                      <thead className="bg-slate-100 text-xs uppercase tracking-[0.1em] text-slate-500">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Fiscal year</th>
                          <th className="px-4 py-3 font-semibold">Salary in period</th>
                          <th className="px-4 py-3 font-semibold">Tax</th>
                          <th className="px-4 py-3 font-semibold">Net salary</th>
                          <th className="px-4 py-3 font-semibold">Applicable slab</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {multiYearResult.yearlyBreakdown.map((item) => (
                          <tr key={item.taxYear.id}>
                            <td className="px-4 py-3 font-semibold text-slate-950">{item.taxYear.label}</td>
                            <td className="px-4 py-3 text-slate-700">{formatCurrency(item.annualSalary)}</td>
                            <td className="px-4 py-3 text-slate-700">{formatCurrency(item.annualTax)}</td>
                            <td className="px-4 py-3 text-slate-700">{formatCurrency(item.netSalary)}</td>
                            <td className="px-4 py-3 text-slate-700">{item.slab.label}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </form>
          )}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <Link to="/contact" className="btn-primary flex-1">
              Review My Salary Tax
            </Link>
            <a className="btn-secondary flex-1" href="#salary-tax-calculator">
              Recalculate
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SlabTable({ selectedTaxYear }) {
  return (
    <div className="mt-5 overflow-x-auto rounded-md border border-slate-200">
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead className="bg-slate-100 text-xs uppercase tracking-[0.1em] text-slate-500">
          <tr>
            <th className="px-4 py-3 font-semibold">Annual income slab</th>
            <th className="px-4 py-3 font-semibold">Fixed tax</th>
            <th className="px-4 py-3 font-semibold">Rate above slab start</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {selectedTaxYear.slabs.map((slab) => (
            <tr key={`${selectedTaxYear.id}-${slab.over}-${slab.max}`}>
              <td className="px-4 py-3 text-slate-700">{slab.label}</td>
              <td className="px-4 py-3 font-semibold text-slate-950">{formatCurrency(slab.base)}</td>
              <td className="px-4 py-3 text-slate-700">
                {slab.rate === 0 ? "Exempt" : `${formatRate(slab.rate)} over ${formatCurrency(slab.over)}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
